import { z } from "zod"

import { reconciliationToleranceCents } from "../analysis/statement-reconciliation.js"
import { domainEventTypes } from "../taxonomy.js"
import { workflowLimits } from "../workflows/vocabulary.js"
import { agentAfterApprovals, agentStatuses } from "./vocabulary.js"

/**
 * The config an agent kind stores, validated here and only here. The jsonb
 * column takes the same posture as `workflows.conditions`: the database holds
 * what this schema accepted, and a constraint could not express these rules
 * without drifting from them.
 */

/** ISO 4217 alpha code, stored upper-cased so comparisons are equalities. */
const currencyCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, "A currency is a 3-letter ISO code.")

export const BillsAgentConfigSchema = z.object({
  /**
   * Bills above this amount always wait for a person. Minor units in the
   * config's own currency, bounded by the same ceiling the workflow amount
   * columns hold so a saved limit is always comparable.
   */
  approveUpToMinor: z
    .number()
    .int()
    .min(0)
    .max(Math.round(workflowLimits.maxAmount * 100)),
  currency: currencyCodeSchema,
  /** The first bill from a vendor always waits for a person. */
  knownVendorsOnly: z.boolean(),
  /**
   * What happens after the agent approves a bill. `post_to_quickbooks`
   * additionally needs the posting prerequisites the workflow action uses;
   * the engine re-checks them on every pass and falls back to keep.
   */
  afterApproval: z.enum(agentAfterApprovals).default("keep"),
})

export type BillsAgentConfig = z.infer<typeof BillsAgentConfigSchema>

/** What a workspace starts with the first time an admin enables the agent. */
export const defaultBillsAgentConfig: BillsAgentConfig = {
  approveUpToMinor: 250_000,
  currency: "USD",
  knownVendorsOnly: true,
  afterApproval: "keep",
}

/**
 * The widest gap a workspace may call balanced. The default is the pipeline's
 * own tolerance, so a statement the product already calls balanced is the
 * statement the agent approves; raising it is a workspace's own decision
 * about rounding it is willing to live with. Capped at $10.00 in minor units:
 * past that the gate stops being a check.
 */
const maxToleranceCents = 1_000

export const StatementsAgentConfigSchema = z.object({
  toleranceCents: z.number().int().min(0).max(maxToleranceCents),
  /** The first statement for an account always waits for a person. */
  requireKnownAccount: z.boolean(),
})

export type StatementsAgentConfig = z.infer<typeof StatementsAgentConfigSchema>

/** What a workspace starts with the first time an admin enables the agent. */
export const defaultStatementsAgentConfig: StatementsAgentConfig = {
  toleranceCents: reconciliationToleranceCents,
  requireKnownAccount: true,
}

/**
 * Every kind's config, as one type. The `agent_configs.config` column holds
 * exactly this, and the registry's schema for the row's kind is what says
 * which arm it is.
 */
export type AgentKindConfig = BillsAgentConfig | StatementsAgentConfig

/**
 * The status an agent config may be written to. The route additionally
 * refuses anything past `off` for a trial; that is an entitlement decision,
 * not a shape decision, so it does not live here.
 */
export const AgentStatusSchema = z.enum(agentStatuses)

/**
 * What the engine reads off a claimed agent outbox message, and nothing
 * else. The outbox payload is jsonb written elsewhere, so it is parsed here
 * rather than trusted; a payload that does not say which document it is
 * about is settled, not retried, because the answer would be the same next
 * time at a cost.
 */
export const AgentTriggerEventSchema = z.object({
  eventId: z.string().uuid().optional(),
  eventType: z.enum(domainEventTypes),
  documentId: z.string().uuid(),
})

export type AgentTriggerEvent = z.infer<typeof AgentTriggerEventSchema>
